/**
 * License state hook — fetches entitlement from bluprint-billing and
 * caches in localStorage for 6h. Re-fetches on mount + every 30 min
 * while the pane is open.
 *
 * Returns 'trial' optimistically if the billing service is unreachable
 * on first call, so users aren't blocked by infra hiccups. A second
 * failed fetch inside the 6h window keeps the last-known-good state.
 *
 * Client ID collision note: cw-api.ts already owns the CW `clientId`
 * HTTP header (the OEL developer UUID). This hook only uses the
 * user-facing `Company ID` / `memberId` fields from cw_config, not
 * `clientId` — per the 2026-04-14 incident lesson.
 */
import { useEffect, useState } from 'react';

export type LicenseStatus = 'active' | 'trial' | 'grace' | 'expired' | 'none';

export interface License {
  status: LicenseStatus;
  plan: 'oel' | 'cml' | 'icl' | 'bundle' | null;
  seats: number | null;
  used_seats: number | null;
  expires_at: string | null;
  app: 'oel' | 'cml' | 'icl';
  /** True when this value came from the local cache rather than a live fetch. */
  fromCache?: boolean;
}

const CACHE_KEY = 'cw_license';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;       // 6h — matches server-side refresh cadence
const POLL_INTERVAL_MS = 30 * 60 * 1000;       // 30 min while pane is open

function billingUrl(): string {
  // Override via Vite env (VITE_BILLING_URL=http://localhost:3010 for local dev)
  return (import.meta.env.VITE_BILLING_URL as string | undefined) || 'https://bp2-billing.bluprint-ai.com';
}

// BAS-2 secure identity (replaces the spoofable client-supplied cw_company_id / cw_member_id —
// closes Owen's probe-1). The add-in already holds the CW member API creds as the base64 Basic
// string in cw_config.auth ("companyId+publicKey:privateKey"). We send that ONLY on the handshake;
// billing validates it against the CW members/company API, derives company+member SERVER-SIDE, and
// returns a short-TTL HMAC token. Every /license read then carries Bearer <token>; identity comes
// from the token claims, never client params. privateKey is never sent except on handshake.
// (Oscar BAS-2 increment-2 contract, frozen 2026-05-30; integration-verified at co-merge with the
// /billing/license/handshake endpoint.)
const TOKEN_KEY = 'cw_license_token';
const TOKEN_SKEW_MS = 30 * 1000;   // refresh this long before stated expiry

function readAuthHeader(): string | null {
  try {
    const raw = localStorage.getItem('cw_config');
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    return typeof cfg.auth === 'string' && cfg.auth ? cfg.auth : null;   // base64 "companyId+publicKey:privateKey"
  } catch {
    return null;
  }
}

// CW region/site the user configured (cw_config.baseUrl). Sent on handshake so billing validates
// the Basic creds against the customer's actual CW region (NA/EU/AU/self-hosted), not always NA.
// NOTE: cw-api.ts getBaseUrl() currently hardcodes the NA /cwapi proxy + ignores this — a latent
// multi-region bug to fix separately; passing it here makes the billing contract region-ready now.
function readBaseUrl(): string | null {
  try {
    const raw = localStorage.getItem('cw_config');
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    return typeof cfg.baseUrl === 'string' && cfg.baseUrl ? cfg.baseUrl : null;
  } catch {
    return null;
  }
}

function readToken(): string | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const { token, expiresAtMs } = JSON.parse(raw);
    if (typeof token !== 'string' || typeof expiresAtMs !== 'number') return null;
    if (Date.now() >= expiresAtMs - TOKEN_SKEW_MS) return null;   // expired / about to
    return token;
  } catch {
    return null;
  }
}

function writeToken(token: string, expiresAt: string | number) {
  // expires_at may arrive as ISO string or unix seconds — normalize to epoch ms.
  let ms: number;
  if (typeof expiresAt === 'number') ms = expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
  else { const t = Date.parse(expiresAt); ms = Number.isFinite(t) ? t : Date.now() + 15 * 60 * 1000; }
  try { localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, expiresAtMs: ms })); } catch { /* best-effort cache */ }
}

// Valid bearer token: cached if live, else handshake (Basic creds → server-derived identity → token).
async function getLicenseToken(force = false): Promise<string | null> {
  if (!force) { const cached = readToken(); if (cached) return cached; }
  const auth = readAuthHeader();
  if (!auth) return null;   // not configured yet (no CW creds)
  const res = await fetch(`${billingUrl()}/billing/license/handshake`, {
    method: 'POST',
    headers: { Authorization: `Basic ${auth}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ cw_baseurl: readBaseUrl() }),   // CW region/site — server validates against it, defaults NA if absent
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`license handshake ${res.status}`);
  const { token, expires_at } = await res.json();
  if (typeof token !== 'string' || !token) throw new Error('license handshake: no token');
  writeToken(token, expires_at);
  return token;
}

async function fetchLicense(app: 'oel' | 'cml' | 'icl'): Promise<License | null> {
  let token = await getLicenseToken();
  if (!token) return null;
  const url = new URL(`${billingUrl()}/billing/license`);
  url.searchParams.set('app', app);   // app selector only — identity is in the token, never query params
  let res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` }, credentials: 'omit' });
  if (res.status === 401) {
    // token expired/invalid → re-handshake once and retry.
    token = await getLicenseToken(true);
    if (!token) return null;
    res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` }, credentials: 'omit' });
  }
  if (!res.ok) throw new Error(`license fetch ${res.status}`);
  return res.json();
}

function readCache(app: string): License | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entries = JSON.parse(raw) as Record<string, { fetchedAt: number; license: License }>;
    const entry = entries[app];
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) return null;
    return { ...entry.license, fromCache: true };
  } catch {
    return null;
  }
}

function writeCache(app: string, license: License) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const entries = raw ? (JSON.parse(raw) as Record<string, { fetchedAt: number; license: License }>) : {};
    entries[app] = { fetchedAt: Date.now(), license: { ...license, fromCache: false } };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {
    // ignore; best-effort caching
  }
}

export function useLicense(app: 'oel' | 'cml' | 'icl'): License | null {
  const [license, setLicense] = useState<License | null>(() => readCache(app));

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const fresh = await fetchLicense(app);
        if (!fresh || cancelled) return;
        writeCache(app, fresh);
        setLicense(fresh);
      } catch {
        // Silent fall-back to whatever we last had. If nothing, optimistically
        // assume 'trial' so users aren't blocked by a transient fetch failure.
        if (!cancelled && !license) {
          setLicense({
            status: 'trial',
            plan: null,
            seats: null,
            used_seats: null,
            expires_at: null,
            app,
            fromCache: false,
          });
        }
      }
    }
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app]);

  return license;
}
